import React, { useCallback, useState } from "react";
import { EditFormElement } from "@kaltura/mediaspace-shared-edit-entry";
import { StyledModal } from "./components/StyleComponents";
import { ButtonClickAnalyticsType, KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics, ButtonAnalyticsProvider } from "@kaltura/mediaspace-shared-hooks";
import { AddFromUrlEditModal } from "./components/AddFromUrlEditModal";
import { KalturaMediaEntry } from "kaltura-typescript-client/api/types/KalturaMediaEntry";
import { baseUrl, translate } from "@kaltura/mediaspace-shared-utils";
import { AddFromUrlConfig, AddFromUrlImportModal } from "@kaltura/mediaspace-shared-add-from-url";
import { AddEntrySaveResponse } from "./types";

interface AddFromUrlProps {
    open: boolean;
    onCancel: () => void;
    fieldsStructure: EditFormElement[];
    preSaveComponents?: KmsTypeComponent[];
    postSaveComponent?: KmsTypeComponent;
    allowedExtensions?: string[];
}

const APPLICATION_FEATURE = "AddFromUrl";
const ANALYTICS_LOCATION = "Modal - Add from URL";

/**
 * AddFromUrl Import Component
 * This component handles the full flow of importing media from a URL:
 * 1. Modal for entering the media URL
 * 2. Server-side creation of a Kaltura entry from the URL
 * 3. Edit form for updating the entry metadata
 * 4. Saving and redirecting to the media page
 */

export const AddFromUrl: React.FC<AddFromUrlProps> = ({
    open,
    onCancel,
    fieldsStructure,
    preSaveComponents,
    postSaveComponent,
    allowedExtensions,
}) => {
    const [entry, setEntry] = useState<KalturaMediaEntry | null>(null);
    const [showEditForm, setShowEditForm] = useState(false);
    const sendButtonAnalytics = useButtonAnalytics();

    // Handle successful import
    const handleSuccess = useCallback(
        (result: AddEntrySaveResponse) => {
            if (!result.entry) {
                return;
            }
            setEntry(result.entry);
            setShowEditForm(true);
        },
        []
    );

    const config: AddFromUrlConfig = {
        urlPlaceholder: translate("e.g. https://media.com/media.mp4"),
        submitUrl: `${baseUrl}/addfromurl/index/upload-url`,
        onSuccess: handleSuccess,
    };

    // Handle closing the edit form
    const handleFormClose = (shouldRedirect = true) => {
        setShowEditForm(false);
        setEntry(null);

        if (onCancel) {
            onCancel();
        }

        // If shouldRedirect is true and uploadUrl is set, redirect to the entry page
        if (shouldRedirect && entry && entry.id) {
            window.location.href = `/media/${entry.id}`;
            return;
        }
        else {
            // Cancel the modal.
            sendButtonAnalytics(
                "Add from URL",
                ButtonClickAnalyticsType.CLOSE,
                undefined,
                APPLICATION_FEATURE,
                ANALYTICS_LOCATION
            );
        }
    };

    const showEdit = showEditForm && !!entry;
    // by default show the import modal, and if import finished successfully - show the edit form
    const modalContent = showEdit ? (
        <AddFromUrlEditModal
            open={showEdit}
            onCancel={() => handleFormClose(false)}
            entry={entry}
            onClose={handleFormClose}
            fieldsStructure={fieldsStructure}
            preSaveComponents={preSaveComponents}
            postSaveComponent={postSaveComponent}
        />
    ) : (
        <AddFromUrlImportModal
            open={open}
            config={config}
            onCancel={() => handleFormClose(false)}
            allowedExtensions={allowedExtensions}
        />
    );

    return (
        <ButtonAnalyticsProvider applicationFeature={APPLICATION_FEATURE} location={ANALYTICS_LOCATION}>
            <StyledModal
                open={open}
                editSizedModal={showEdit}
                onClose={(_, reason) => {
                    if (reason !== "backdropClick") {
                        onCancel();
                    }
                }}
            >
                {modalContent}
            </StyledModal>
        </ButtonAnalyticsProvider>
    );
};
